import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Can, Drawer, EmptyState, JsonView, PageHeader, formatDateTime, type BadgeTone } from '@/kit/ui'
import { useFlowStore } from '@/store/flowStore'
import { api, ApiError, apiMode } from '@/lib/hubApi'
import { useHubStore } from '@/store/hubStore'
import { useSettingsStore } from '@/store/settingsStore'
import type { AnswerKind, WorkflowRun } from '@/types'

const TONE: Record<AnswerKind, BadgeTone> = { offer: 'success', approved: 'success', redirect: 'info', pending: 'warning', rejected: 'gray', duplicate: 'warning', error: 'danger' }
const PERIODS = [['1', 'Today'], ['7', '7 days'], ['30', '30 days']] as const
/** Runs a page (owner 2026-11-05: pages with a cursor, the log grows with every lender). */
const PAGE = 50

/** Where a page starts: after this run (its time and id); null = the newest. */
type Cursor = { at: string; id: string } | null

/** The period's first moment: Today = since midnight, else the last N days. */
const fromOf = (period: string) => (period === '1' ? new Date(new Date().setHours(0, 0, 0, 0)) : new Date(Date.now() - Number(period) * 86_400_000)).toISOString()

/** One page from the Hub: the filters on the server, PAGE + 1 asked so "Older" knows whether there is more. */
async function fetchPage(f: { period: string; lender: string; answer: string }, after: Cursor) {
  const q = new URLSearchParams({ days: f.period, from: fromOf(f.period), limit: String(PAGE + 1) })
  if (f.lender !== 'all') q.set('lender', f.lender)
  if (f.answer !== 'all') q.set('answer', f.answer)
  if (after) { q.set('before', after.at); q.set('beforeId', after.id) }
  const rows = await api<WorkflowRun[]>('GET', `/runs?${q}`)
  return { rows: rows.slice(0, PAGE), more: rows.length > PAGE }
}

/** GET /admin/v1/runs/{id}/lender-reply: the Gateway log of one run (2026-11-02). */
type LenderReply = {
  logId: string
  at: string
  calls: number
  nodes: { node: string; endpoint: string; calls: { call: { method: string; url: string; headers?: Record<string, string>; body?: unknown }; reply: { status: number; body?: unknown } }[] }[]
}

/** GET /admin/v1/runs/{id}/lender-reply/kept: whether the Gateway log is kept and, if not, why (no contents). */
type Kept = { kept: boolean; why: 'kept' | 'sample' | 'before_log' | 'expired' | 'no_call'; calls: number; at: string | null }

/** Why there is nothing behind "Show lender reply" (owner 2026-11-05: the old red "Not kept" read like a fault). */
const NOT_KEPT: Record<Exclude<Kept['why'], 'kept'>, string> = {
  sample: 'Practice run: this test call used the saved sample replies, so the lender was not called and there is no reply to keep.',
  before_log: 'This run is older than the Gateway log (it began on 2 Nov 2026, 00:41), so its replies were never kept.',
  expired: 'Kept 30 days only: this run is older, so its replies were deleted.',
  no_call: 'The run stopped before any call reached the lender, so there is no reply to keep.',
}

/** Kept or not, read when a run is opened (cheap; nothing of the applicant, not audited). null = not known (demo, error). */
function useKept(runId: string | null) {
  const [kept, setKept] = useState<Kept | null>(null)
  useEffect(() => {
    setKept(null)
    if (!runId || !apiMode()) return
    let live = true
    api<Kept>('GET', `/runs/${encodeURIComponent(runId)}/lender-reply/kept`).then((k) => { if (live) setKept(k) }, () => {})
    return () => { live = false }
  }, [runId])
  return kept
}

/** The lender's exact replies of one run, opened on demand (admins; each read is in the audit log). */
function LenderReplies({ runId, kept }: { runId: string; kept: Kept | null }) {
  const [state, setState] = useState<{ loading: boolean; data: LenderReply | null; error: string | null }>({ loading: false, data: null, error: null })
  useEffect(() => setState({ loading: false, data: null, error: null }), [runId])
  const load = async () => {
    setState({ loading: true, data: null, error: null })
    try {
      setState({ loading: false, data: await api<LenderReply>('GET', `/runs/${encodeURIComponent(runId)}/lender-reply`), error: null })
    } catch (err) {
      setState({ loading: false, data: null, error: err instanceof ApiError && err.status === 404 ? 'Not kept for this run: only real calls made since the Gateway log came (2 Nov 2026) are kept, 30 days at most. Test calls with sample replies have none.' : (err as Error).message })
    }
  }
  if (!apiMode()) return <p className="text-2xs text-muted">The Gateway log is read from the Hub API (not in demo mode).</p>
  if (kept && !kept.kept) return <p className="text-2xs text-muted" data-testid="run-reply-not-kept">{NOT_KEPT[kept.why as Exclude<Kept['why'], 'kept'>]}</p>
  if (!state.data) return (
    <div className="space-y-1">
      <button type="button" className="btn btn-secondary btn-sm" disabled={state.loading} onClick={() => void load()} data-testid="run-lender-reply">{state.loading ? 'Opening…' : 'Show lender reply (JSON)'}</button>
      <p className="text-2xs text-muted">The request and the lender's exact reply of each call. They carry the applicant's details: opening them is written in the audit log.</p>
      {state.error && <p className="text-2xs text-red-600">{state.error}</p>}
    </div>
  )
  return (
    <div className="space-y-3" data-testid="run-lender-replies">
      {state.data.nodes.map((n, i) => (
        <section key={i} className="rounded border border-slate-200 p-2 dark:border-slate-700">
          <h4 className="text-xs font-semibold">{n.node} <span className="mono text-muted">{n.endpoint}</span></h4>
          {n.calls.map((c, j) => (
            <div key={j} className="mt-2 space-y-2">
              <div className="mono text-2xs">{c.call.method} {c.call.url} → <b className={c.reply.status >= 400 || c.reply.status === 0 ? 'text-red-600' : ''}>{c.reply.status}</b>{n.calls.length > 1 ? ` · call ${j + 1} of ${n.calls.length}` : ''}</div>
              <JsonView title="Lender reply" value={c.reply.body ?? null} maxHeight={420} />
              <details>
                <summary className="cursor-pointer text-2xs text-muted">Request sent (keys hidden)</summary>
                <JsonView value={{ headers: c.call.headers ?? {}, body: c.call.body ?? null }} maxHeight={320} />
              </details>
            </div>
          ))}
        </section>
      ))}
    </div>
  )
}

/** Workflow runs (F3-4): the run log, not a lead store. Reference ids only, PI masked, kept for the retention in Settings. */
export function RunsScreen() {
  const runs = useFlowStore((s) => s.runs)
  const lenders = useHubStore((s) => s.lenders)
  const days = useSettingsStore((s) => s.settings.runLogDays)
  const [lender, setLender] = useState('all')
  const [period, setPeriod] = useState<string>('30')
  const [answer, setAnswer] = useState('all')
  const [open, setOpen] = useState<WorkflowRun | null>(null)
  const kept = useKept(open?.id ?? null)
  // pages: cursors[i] = where page i starts (null = the newest); a filter change goes back to the first page
  const [cursors, setCursors] = useState<Cursor[]>([null])
  const [page, setPage] = useState<{ rows: WorkflowRun[]; more: boolean; loading: boolean; error: string | null }>({ rows: [], more: false, loading: false, error: null })
  useEffect(() => setCursors([null]), [lender, period, answer])
  const at = cursors.length - 1

  // on the API the log is the Hub's: each page is read from it (the filters on the server)
  useEffect(() => {
    if (!apiMode()) return
    let live = true
    setPage((p) => ({ ...p, loading: true, error: null }))
    fetchPage({ period, lender, answer }, cursors[at] ?? null).then(
      (r) => { if (live) setPage({ ...r, loading: false, error: null }) },
      (err: Error) => { if (live) setPage({ rows: [], more: false, loading: false, error: err.message }) },
    )
    return () => { live = false }
  }, [cursors, at, period, lender, answer])

  // the demo (no API): the store's runs, filtered and cut into the same pages
  const demo = useMemo(() => {
    const from = new Date(fromOf(period)).getTime()
    return runs.filter((r) => (lender === 'all' || r.lenderSlug === lender) && (answer === 'all' || r.answer === answer) && new Date(r.at).getTime() >= from).sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id))
  }, [runs, lender, period, answer])
  const shown = apiMode() ? page.rows : demo.slice(at * PAGE, (at + 1) * PAGE)
  const more = apiMode() ? page.more : demo.length > (at + 1) * PAGE
  const older = () => { const last = shown[shown.length - 1]; if (last && more) setCursors((c) => [...c, { at: last.at, id: last.id }]) }
  const newer = () => setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c))
  const name = (slug: string) => lenders.find((l) => l.slug === slug)?.name ?? slug

  return (
    <>
      <PageHeader
        title="Workflow runs"
        subtitle={`Run log, not a lead store: reference ids only, PI masked, kept ${days} days`}
        actions={
          <div className="flex flex-wrap gap-2">
            <select className="input w-auto" aria-label="Lender" value={lender} onChange={(e) => setLender(e.target.value)}>
              <option value="all">All lenders</option>
              {lenders.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
            </select>
            <select className="input w-auto" aria-label="Answer" value={answer} onChange={(e) => setAnswer(e.target.value)}>
              <option value="all">Every answer</option>
              {Object.keys(TONE).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <select className="input w-auto" aria-label="Period" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        }
      />
      {page.error && <p className="text-xs text-red-600">The runs could not be read: {page.error}</p>}
      {shown.length === 0 ? (page.loading ? <p className="text-xs text-muted">Loading…</p> : <EmptyState title="No runs in this period" description="Runs come from the app, the website and Test calls in 3.5." />) : (
        <div className="card-flush overflow-x-auto">
          <table className="table">
            <thead><tr><th>When</th><th>Endpoint</th><th>Versions</th><th>Steps</th><th className="text-right">Time</th><th>Answer</th><th>Ref (app)</th><th>From</th></tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className="cursor-pointer" onClick={() => setOpen(r)} data-testid={`run-${r.id}`}>
                  <td className="mono whitespace-nowrap">{formatDateTime(r.at)}</td>
                  <td className="mono">{r.endpointId}</td>
                  <td className="mono whitespace-nowrap">flow {r.workflowVersion} / api {r.batchVersion}</td>
                  <td className="text-2xs">{r.steps.map((s) => `${s.node} ${s.outcome === 'error' ? '✗' : '✓'}${s.replyCode && s.outcome !== 'ok' ? ` ${s.replyCode}` : ''}`).join(' · ')}</td>
                  <td className="text-right">{(r.ms / 1000).toFixed(1)} s</td>
                  <td><Badge tone={TONE[r.answer]}>{r.answer}</Badge></td>
                  <td className="mono">{r.appRef}</td>
                  <td className="text-2xs">{r.source}{r.env === 'uat' ? ' · UAT' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(at > 0 || more) && (
        <div className="flex items-center justify-end gap-2 text-xs" data-testid="runs-pager">
          <span className="text-muted">Page {at + 1} · {shown.length} run{shown.length === 1 ? '' : 's'}{page.loading ? ' · loading…' : ''}</span>
          <button type="button" className="btn btn-secondary btn-sm" disabled={at === 0 || page.loading} onClick={newer} data-testid="runs-newer">← Newer</button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!more || page.loading} onClick={older} data-testid="runs-older">Older →</button>
        </div>
      )}
      <Drawer isOpen={open !== null} onClose={() => setOpen(null)} size="xl" title={`Run ${open?.appRef ?? ''}`} subtitle={open ? `${open.endpointId} · ${formatDateTime(open.at)} · ${open.env}` : ''}>
        {open && (
          <div className="space-y-3 text-xs" data-testid="run-drawer">
            <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1">
              <dt className="text-muted">Lender</dt><dd><Link to={`/lenders/${open.lenderSlug}/api/endpoints?ep=${encodeURIComponent(open.endpointId)}`}>{name(open.lenderSlug)}</Link></dd>
              <dt className="text-muted">Workflow · batch</dt><dd className="mono">{open.workflowVersion} · batchApi {open.batchVersion}</dd>
              <dt className="text-muted">Answer</dt><dd><Badge tone={TONE[open.answer]}>{open.answer}</Badge> in {(open.ms / 1000).toFixed(2)} s</dd>
              <dt className="text-muted">Gateway log</dt><dd>{kept && !kept.kept
                ? <span className="text-muted">none for this run ({kept.why === 'sample' ? 'practice run, lender not called' : kept.why === 'before_log' ? 'before the Gateway log began' : kept.why === 'expired' ? 'older than 30 days' : 'no call reached the lender'})</span>
                : <><span className="mono">{open.logId}</span> <span className="text-muted">({kept?.kept ? `${kept.calls} call${kept.calls === 1 ? '' : 's'} kept: ` : ''}the lender's exact reply, sealed, 30 days, admins)</span></>}</dd>
            </dl>
            <ol className="space-y-1">
              {open.steps.map((s, i) => (
                <li key={i} className="rounded border border-slate-200 px-2 py-1 dark:border-slate-700">
                  <b>{s.node}</b> <code className={s.outcome === 'error' ? 'text-red-600' : ''}>→ {s.outcome}</code> <span className="text-muted">{s.ms ? `${s.ms} ms` : ''}</span>
                  {s.call && <div className="mono text-muted">{s.call}</div>}
                  {s.replyCode && <div className="text-2xs text-muted">lender code {s.replyCode}</div>}
                  {s.note && <div className="text-2xs text-muted">{s.note}</div>}
                </li>
              ))}
            </ol>
            <p className="text-2xs text-muted">No name, mobile, PAN or date of birth is kept in the run log: the Hub holds lenders, not leads. The app keeps the application under its own reference.</p>
            <Can permission="gateway.manage">
              <section className="border-t border-slate-200 pt-3 dark:border-slate-700">
                <h3 className="mb-1 text-xs font-semibold">Gateway log</h3>
                <LenderReplies runId={open.id} kept={kept} />
              </section>
            </Can>
          </div>
        )}
      </Drawer>
    </>
  )
}
